/**
 * KAN-828: одно правило "может ли пользователь менять менеджера клиента".
 * Тем же правилом решает backend (PATCH clients/<id>/ manager_id); фронт
 * только не предлагает то, что backend отклонит.
 *   - владелец и оператор: меняют всегда;
 *   - менеджер: клиент ничей или свой -> назначить любого, себя или снять;
 *     клиент другого менеджера -> нельзя (подсказка LOCKED_BY_OTHER_HINT);
 *   - старший менеджер (is_senior_manager из /auth/me): меняет любого клиента.
 * "Взять себе" - у менеджера с записью справочника, когда клиент ещё не его
 * и менять можно. reassignsAny: пользователь переназначает любых клиентов
 * (владелец, оператор, старший менеджер), например выбирает менеджера в окне
 * "Отработано".
 * KAN-869: обычный менеджер передаёт своего клиента другому менеджеру
 * (canHandOffClient, PATCH clients/<id>/ manager_id).
 */
import { isManagerUser } from './managerAccess';

export const LOCKED_BY_OTHER_HINT = 'Назначен другой менеджер';

export interface ClientManagerRights {
  /** Можно сменить или снять менеджера клиента. */
  canChange: boolean;
  /** Показывать "Взять себе". */
  canTakeSelf: boolean;
  /** Запись справочника текущего пользователя-менеджера; null у остальных. */
  selfId: string | null;
  /** Почему менять нельзя; null: можно. */
  lockedHint: string | null;
  /** Переназначает любых клиентов (не только ничьих и своих). */
  reassignsAny: boolean;
}

interface RightsUser {
  role?: string;
  manager_id?: string | null;
  is_senior_manager?: boolean;
}

const FREE: ClientManagerRights = {
  canChange: true,
  canTakeSelf: false,
  selfId: null,
  lockedHint: null,
  reassignsAny: true,
};

export function clientManagerRights(
  user: RightsUser | null | undefined,
  currentManagerId: string | null | undefined
): ClientManagerRights {
  if (!isManagerUser(user)) return FREE;
  const selfId = user?.manager_id ?? null;
  const current = currentManagerId ?? null;
  const ownedByOther = current !== null && current !== selfId;
  const senior = !!user?.is_senior_manager;
  const locked = ownedByOther && !senior;
  return {
    canChange: !locked,
    canTakeSelf: !locked && selfId !== null && current !== selfId,
    selfId,
    lockedHint: locked ? LOCKED_BY_OTHER_HINT : null,
    reassignsAny: senior,
  };
}

/** KAN-869: подпись кнопки передачи своего клиента другому менеджеру. */
export const HAND_OFF_LABEL = 'Передать';

/** KAN-869: тост после передачи клиента. */
export function handedOffText(fullName: string | null | undefined): string {
  return fullName ? `Клиент передан: ${fullName}` : 'Клиент передан';
}

/**
 * KAN-869: обычный менеджер (не переназначает любых) на своём клиенте может
 * передать его другому менеджеру. Владелец, оператор и старший менеджер
 * назначают через окно "Назначить", им передача не нужна.
 */
export function canHandOffClient(
  rights: ClientManagerRights,
  currentManagerId: string | null | undefined
): boolean {
  return !rights.reassignsAny && rights.selfId !== null && (currentManagerId ?? null) === rights.selfId;
}
